import { SS_LAST_ACTIVITY_AT } from '@/constants/common';

import { saveSessionStorage } from '@/utils/browser';
import { isCaymanDomain } from '@/utils/domain';

/**
 * Central session-activity tracker for Cayman auth policy.
 *
 * Responsibilities:
 * - Track when the user is actively interacting with the app.
 * - Track when the session becomes inactive (hidden tab or browser blur).
 * - Expose pure helpers used by timeout/refresh logic.
 *
 * This module is intentionally framework-agnostic (no redux/react usage)
 * so both hooks and axios guards can reuse the same inactivity rules.
 */
const SESSION_TIMEOUT_MS = 10 * 60 * 1000;

const USER_ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'focus',
  'visibilitychange' as keyof WindowEventMap,
];

let isTrackingStarted = false;
let lastActivityAt = Date.now();

saveSessionStorage(
  SS_LAST_ACTIVITY_AT,
  new Date(lastActivityAt).toLocaleString(),
);

const subscribers = new Set<() => void>();

function notifySubscribers() {
  subscribers.forEach(subscriber => {
    subscriber();
  });
}

function markVisibleActivity(now = Date.now()) {
  // Any visible interaction re-activates the session.
  lastActivityAt = now;
  saveSessionStorage(SS_LAST_ACTIVITY_AT, new Date(now).toLocaleString());
  notifySubscribers();
}

function onActivity() {
  if (document.visibilityState === 'hidden') {
    return;
  }

  markVisibleActivity();
}

export function startSessionActivityTracking() {
  // Current rollout scope is Cayman domain. Guard keeps behavior tenant-scoped.
  if (!isCaymanDomain) {
    return;
  }

  if (globalThis.window === undefined || isTrackingStarted) {
    return;
  }

  isTrackingStarted = true;
  lastActivityAt = Date.now();

  USER_ACTIVITY_EVENTS.forEach(eventName => {
    globalThis.window.addEventListener(eventName, onActivity, {
      passive: true,
    });
  });
}

export function stopSessionActivityTracking() {
  if (globalThis.window === undefined || !isTrackingStarted) {
    return;
  }

  isTrackingStarted = false;
  USER_ACTIVITY_EVENTS.forEach(eventName => {
    globalThis.window.removeEventListener(eventName, onActivity);
  });
}

/**
 * Data flow for current use case: Listeners (useSessionTimeout) -> Handlers (useSessionTimeout -> scheduleSignOut)
 * @param handler - The function to call when the session activity changes.
 * @returns A function to unsubscribe from the session activity changes.
 */
export function subscribeSessionActivity(handler: () => void) {
  subscribers.add(handler);

  return () => {
    subscribers.delete(handler);
  };
}

export function getSessionTimeoutMs() {
  return SESSION_TIMEOUT_MS;
}

export function isUserActive(now = Date.now()) {
  return now - lastActivityAt < SESSION_TIMEOUT_MS;
}

export function getInactiveDeadlineAt() {
  // If hidden/blurred, countdown starts there; otherwise from last interaction.
  return lastActivityAt + SESSION_TIMEOUT_MS;
}

export function isInactiveForSessionTimeout(now = Date.now()) {
  return now >= getInactiveDeadlineAt();
}
